import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutGrid, Users, FolderKanban, Inbox, FileText, Receipt, LogOut, Scale, Banknote, FileSignature, ShieldCheck, Megaphone } from "lucide-react";

// Phase OTP-3 — Leads collapsed into Matters; the "New" filter on Matters
// replaces the old Leads page. Role gating unchanged.
const NAV = [
  { to: "/admin",              label: "Overview",   icon: LayoutGrid,    exact: true, roles: ["admin", "back_office", "lawyer"] },
  { to: "/admin/matters?status=new", label: "New Matters",  icon: Inbox,                       roles: ["admin", "back_office"] },
  { to: "/admin/proformas",    label: "Pro-formas", icon: FileSignature,               roles: ["admin", "back_office"] },
  { to: "/admin/matters",      label: "Matters",    icon: FolderKanban,                roles: ["admin", "back_office", "lawyer"] },
  { to: "/admin/clients",      label: "Clients",    icon: Users,                       roles: ["admin", "back_office", "lawyer"] },
  { to: "/admin/templates",    label: "Templates",  icon: FileText,                    roles: ["admin", "back_office"] },
  { to: "/admin/invoices",     label: "Invoices",   icon: Receipt,                     roles: ["admin", "back_office"] },
  { to: "/admin/campaign-kit", label: "Campaigns",  icon: Megaphone,                   roles: ["admin", "back_office"] },
  { to: "/admin/pricing",      label: "Pricing",    icon: Banknote,                    roles: ["admin"] },
  { to: "/admin/users",        label: "Team",       icon: ShieldCheck,                 roles: ["admin"] },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const onLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (item) =>
    item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const role = user?.role || "back_office";
  const visibleNav = NAV.filter((n) => n.roles.includes(role));

  return (
    <div className="min-h-screen bg-zinc-50 flex" data-testid="admin-layout">
      <aside className="w-64 bg-zinc-900 text-white flex flex-col">
        <Link to="/admin" className="px-6 py-5 flex items-center gap-2 border-b border-zinc-800">
          <img
            src="/eh-logo-white.png"
            alt="E Holtzhausen Inc — Attorneys and Conveyancers"
            className="h-12 w-auto object-contain"
          />
        </Link>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                  active
                    ? "bg-white text-zinc-900"
                    : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="px-3 pb-6 mt-auto">
          <div className="px-3 py-3 mb-2 border-t border-zinc-800 pt-4">
            <div className="text-xs text-zinc-400">Signed in as</div>
            <div className="text-sm font-medium truncate" data-testid="admin-user-email">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1" data-testid="admin-user-role">{role}</div>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
